import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { PageContainer, PageTitleBadge, ModernButton, StatCard } from "../components";
import { useTranslation } from "../context/LanguageContext";
import { GiCow } from "react-icons/gi";
import { FaArrowLeft } from "react-icons/fa";
import "../styles/common.css";
import "./FicheAnimal.css";

export default function FicheAnimal() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [fiche, setFiche] = useState(null);
  const [animalData, setAnimalData] = useState(null); // Données brutes de l'animal
  const [troupeauData, setTroupeauData] = useState(null); // Données du troupeau
  const [ranchData, setRanchData] = useState(null); // Données du ranch de provenance
  const [users, setUsers] = useState([]); // Liste des utilisateurs pour résoudre les IDs
  const [animaux, setAnimaux] = useState([]); // Liste des animaux pour résoudre les IDs des parents
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFiche = async () => {
      try {
        // Récupérer les données de l'animal, la liste des utilisateurs et la liste des animaux en parallèle
        const [animalResponse, usersResponse, animauxResponse] = await Promise.all([
          api.get(`/api/animaux/${id}/`),
          api.get("/api/users/"),
          api.get("/api/animaux/")
        ]);
        const animal = animalResponse.data;
        setAnimalData(animal);
        setUsers(usersResponse.data);
        setAnimaux(animauxResponse.data);
        console.log("Données de l'animal:", animal); // Debug
        console.log("Liste des utilisateurs:", usersResponse.data); // Debug
        console.log("Liste des animaux:", animauxResponse.data); // Debug
        
        // Ensuite, essayer de récupérer la fiche détaillée
        let ficheData = null;
        try {
          const ficheResponse = await api.get(`/api/animaux/${id}/fiche/`);
          ficheData = ficheResponse.data;
          console.log("Données de la fiche:", ficheData); // Debug
        } catch (ficheErr) {
          console.warn("L'endpoint /fiche/ n'est pas disponible, construction d'une fiche basique:", ficheErr);
          // Construire une fiche basique à partir des données de l'animal
          ficheData = {
            "Informations générales": {
              matricule: animal.matricule || "",
              troupeau: (animal.troupeau && typeof animal.troupeau === 'object' && animal.troupeau.nom) 
                ? animal.troupeau.nom 
                : "Chargement...",
              race: animal.race || "Non renseigné",
              sexe: animal.sexe === "M" ? "Mâle" : animal.sexe === "F" ? "Femelle" : "Non renseigné",
              age: animal.age || "Non renseigné",
              statut: animal.statut || "Actif",
              catégorie: animal.categorie_nom || animal.categorie || "Non renseigné",
              robe: animal.robe || "Non renseigné",
              ranch_provenance: animal.ranch_provenance ? (typeof animal.ranch_provenance === 'object' 
                ? animal.ranch_provenance.nom || "Non spécifié"
                : "Non spécifié") : null
            },
            "Informations généalogiques": {
              geniteur: null, // Sera résolu plus tard via useEffect
              genitrice: null, // Sera résolu plus tard via useEffect
              descendants: []
            },
            "Suivi sanitaire": [],
            "Suivi reproduction": [],
            "Historique événements": [],
            "Valeur estimée": animal.valeur || 0
          };
        }
        
        setFiche(ficheData);
        
        // Si le troupeau n'est qu'un ID, récupérer ses détails
        if (animal.troupeau && typeof animal.troupeau === 'number') {
          try {
            const troupeauResponse = await api.get(`/api/troupeaux/${animal.troupeau}/`);
            console.log("Données du troupeau:", troupeauResponse.data); // Debug
            const troupeau = troupeauResponse.data;
            setTroupeauData(troupeau);
            
            // Mettre à jour le nom du troupeau dans la fiche
            if (ficheData && ficheData["Informations générales"] && troupeau.nom) {
              setFiche(prev => ({
                ...prev,
                "Informations générales": {
                  ...prev["Informations générales"],
                  troupeau: troupeau.nom
                }
              }));
            }
          } catch (err) {
            console.error("Erreur lors du chargement du troupeau:", err);
          }
        } else if (animal.troupeau && typeof animal.troupeau === 'object') {
          setTroupeauData(animal.troupeau);
          // Mettre à jour le nom du troupeau dans la fiche
          if (ficheData && ficheData["Informations générales"] && animal.troupeau.nom) {
            setFiche(prev => ({
              ...prev,
              "Informations générales": {
                ...prev["Informations générales"],
                troupeau: animal.troupeau.nom
              }
            }));
          }
        }
        
        // Si le ranch de provenance n'est qu'un ID, récupérer ses détails
        if (animal.ranch_provenance && typeof animal.ranch_provenance === 'number') {
          try {
            const ranchResponse = await api.get(`/api/ranches/${animal.ranch_provenance}/`);
            console.log("Données du ranch:", ranchResponse.data); // Debug
            setRanchData(ranchResponse.data);
          } catch (err) {
            console.error("Erreur lors du chargement du ranch:", err);
          }
        } else if (animal.ranch_provenance && typeof animal.ranch_provenance === 'object') {
          setRanchData(animal.ranch_provenance);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Erreur de l'API:", err);
        console.error("ID utilisé:", id);
        console.error("Réponse d'erreur:", err.response);
        
        // Message d'erreur plus détaillé
        let errorMessage = "Impossible de charger la fiche de l'animal.";
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = "L'animal demandé n'existe pas ou la fiche n'est pas disponible.";
          } else if (err.response.status === 403) {
            errorMessage = "Vous n'avez pas la permission d'accéder à cette fiche.";
          } else if (err.response.status >= 500) {
            errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
          } else {
            errorMessage = err.response.data?.detail || err.response.data?.message || errorMessage;
          }
        } else if (err.request) {
          errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchFiche();
    } else {
      setError("ID de l'animal manquant.");
      setLoading(false);
    }
  }, [id]);

  // Mettre à jour la fiche quand troupeauData est disponible
  useEffect(() => {
    if (troupeauData && troupeauData.nom) {
      // Utiliser une mise à jour fonctionnelle pour lire l'état actuel et comparer
      setFiche(prev => {
        // Retourner prev si "Informations générales" n'existe pas
        if (!prev || !prev["Informations générales"]) {
          return prev;
        }

        // Lire la valeur actuelle depuis prev
        const currentTroupeau = prev["Informations générales"].troupeau;
        
        // Vérifier si le troupeau doit être mis à jour (falsy, "Non assigné" ou "Chargement...")
        const needsUpdate = !currentTroupeau || 
                           currentTroupeau === "Non assigné" || 
                           currentTroupeau === "Chargement..." ||
                           currentTroupeau !== troupeauData.nom;

        // Ne mettre à jour que si nécessaire
        if (needsUpdate) {
          return {
            ...prev,
            "Informations générales": {
              ...prev["Informations générales"],
              troupeau: troupeauData.nom
            }
          };
        }

        // Retourner prev inchangé si rien n'a changé
        return prev;
      });
    }
  }, [troupeauData]);

  // Mettre à jour la fiche avec les informations des parents quand animaux est disponible
  useEffect(() => {
    if (animaux.length > 0 && animalData) {
      // Résoudre le géniteur (père) - calculé une seule fois depuis animaux et animalData
      let resolvedGeniteur = null;
      if (animalData.pere) {
        if (typeof animalData.pere === 'object' && animalData.pere.matricule) {
          resolvedGeniteur = animalData.pere.matricule;
        } else if (typeof animalData.pere === 'number') {
          const pereAnimal = animaux.find(a => a.id === animalData.pere);
          if (pereAnimal) {
            resolvedGeniteur = pereAnimal.matricule;
          }
        }
      }

      // Résoudre la génitrice (mère) - calculé une seule fois depuis animaux et animalData
      let resolvedGenitrice = null;
      if (animalData.mere) {
        if (typeof animalData.mere === 'object' && animalData.mere.matricule) {
          resolvedGenitrice = animalData.mere.matricule;
        } else if (typeof animalData.mere === 'number') {
          const mereAnimal = animaux.find(a => a.id === animalData.mere);
          if (mereAnimal) {
            resolvedGenitrice = mereAnimal.matricule;
          }
        }
      }

      // Utiliser une mise à jour fonctionnelle pour lire l'état actuel et comparer
      setFiche(prev => {
        // Retourner prev si "Informations généalogiques" n'existe pas
        if (!prev || !prev["Informations généalogiques"]) {
          return prev;
        }

        // Lire les valeurs actuelles depuis prev
        const currentGeniteur = prev["Informations généalogiques"].geniteur;
        const currentGenitrice = prev["Informations généalogiques"].genitrice;

        // Comparer les valeurs résolues avec les valeurs actuelles
        const geniteurChanged = resolvedGeniteur && resolvedGeniteur !== currentGeniteur;
        const genitriceChanged = resolvedGenitrice && resolvedGenitrice !== currentGenitrice;
        const geniteurWasNull = currentGeniteur === null && resolvedGeniteur;
        const genitriceWasNull = currentGenitrice === null && resolvedGenitrice;

        // Ne mettre à jour que si quelque chose a changé
        if (geniteurChanged || genitriceChanged || geniteurWasNull || genitriceWasNull) {
          return {
            ...prev,
            "Informations généalogiques": {
              ...prev["Informations généalogiques"],
              geniteur: resolvedGeniteur || "Non défini",
              genitrice: resolvedGenitrice || "Non définie"
            }
          };
        }

        // Retourner prev inchangé si rien n'a changé
        return prev;
      });
    }
  }, [animaux, animalData]);

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">Chargement de la fiche...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title="ERREUR" icon={<GiCow />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <ModernButton
            variant="secondary"
            icon={<FaArrowLeft />}
            onClick={() => navigate(-1)}
          >
            Retour
          </ModernButton>
        </div>
      </PageContainer>
    );
  }

  // Fonction helper pour obtenir le nom du berger
  const getBergerName = () => {
    // Essayer depuis la fiche d'abord
    if (fiche?.Responsable) return fiche.Responsable;
    if (fiche?.["Informations générales"]?.berger) return fiche["Informations générales"].berger;
    
    // Essayer depuis troupeauData (données récupérées séparément)
    if (troupeauData?.user) {
      const user = troupeauData.user;
      if (typeof user === 'object') {
        const parts = [];
        if (user.first_name) parts.push(user.first_name);
        if (user.last_name) parts.push(user.last_name);
        if (parts.length > 0) {
          return parts.join(" ");
        }
        if (user.username) return user.username;
      }
      // Si user est un ID (nombre), chercher dans la liste des utilisateurs
      if (typeof user === 'number') {
        const foundUser = users.find(u => u.id === user);
        if (foundUser) {
          const parts = [];
          if (foundUser.first_name) parts.push(foundUser.first_name);
          if (foundUser.last_name) parts.push(foundUser.last_name);
          if (parts.length > 0) {
            return parts.join(" ");
          }
          if (foundUser.username) return foundUser.username;
        }
      }
    }
    
    // Essayer depuis les données de l'animal (troupeau.user)
    if (animalData?.troupeau?.user) {
      const user = animalData.troupeau.user;
      if (typeof user === 'object') {
        const parts = [];
        if (user.first_name) parts.push(user.first_name);
        if (user.last_name) parts.push(user.last_name);
        if (parts.length > 0) {
          return parts.join(" ");
        }
        if (user.username) return user.username;
      }
      // Si user est un ID (nombre), chercher dans la liste des utilisateurs
      if (typeof user === 'number') {
        const foundUser = users.find(u => u.id === user);
        if (foundUser) {
          const parts = [];
          if (foundUser.first_name) parts.push(foundUser.first_name);
          if (foundUser.last_name) parts.push(foundUser.last_name);
          if (parts.length > 0) {
            return parts.join(" ");
          }
          if (foundUser.username) return foundUser.username;
        }
      }
    }
    
    // Essayer depuis la fiche (structure alternative)
    if (fiche?.["Informations générales"]?.troupeau?.user) {
      const user = fiche["Informations générales"].troupeau.user;
      if (typeof user === 'object') {
        const parts = [];
        if (user.first_name) parts.push(user.first_name);
        if (user.last_name) parts.push(user.last_name);
        if (parts.length > 0) {
          return parts.join(" ");
        }
        if (user.username) return user.username;
      }
    }
    
    return "Non renseigné";
  };

  // Fonction helper pour obtenir le nom du ranch de provenance
  const getRanchProvenanceName = () => {
    // Essayer depuis ranchData (données récupérées séparément)
    if (ranchData) {
      return ranchData.nom || "Non spécifié";
    }
    
    // Essayer depuis les données de l'animal
    if (animalData?.ranch_provenance) {
      const ranch = animalData.ranch_provenance;
      if (typeof ranch === 'object') {
        return ranch.nom || "Non spécifié";
      }
      if (typeof ranch === 'number') {
        return `Ranch ID: ${ranch}`;
      }
    }
    
    // Essayer depuis la fiche
    if (fiche?.["Informations générales"]?.ranch_provenance) {
      const ranch = fiche["Informations générales"].ranch_provenance;
      if (typeof ranch === 'object') {
        return ranch.nom || "Non spécifié";
      }
      if (typeof ranch === 'string') return ranch;
    }
    
    return "Non spécifié";
  };

  if (!fiche) {
    return (
      <PageContainer>
        <PageTitleBadge title="FICHE DÉTAILLÉE" icon={<GiCow />} />
        <div className="empty-message-container">
          <p className="empty-message">Aucune fiche disponible pour cet animal.</p>
          <ModernButton
            variant="secondary"
            icon={<FaArrowLeft />}
            onClick={() => navigate(-1)}
          >
            Retour
          </ModernButton>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge 
        title={`${t.pages["/animaux"] || "FICHE"} : ${fiche?.["Informations générales"]?.matricule || t.animal}`} 
        icon={<GiCow />} 
      />

      {/* Section Statistiques avec StatCard */}
      <div className="fiche-stats-section">
        <h2 className="section-title">{t.statistics}</h2>
        <div className="kpi-grid-fiche">
          <StatCard 
            type="valeur" 
            label={t.estimatedValueFull} 
            value={fiche?.["Valeur estimée"] ? `${fiche["Valeur estimée"].toLocaleString()} FCFA` : t.notProvided} 
            variant="dashboard" 
            showIcon={false} 
            className="card-tall" 
          />
          <StatCard 
            type="naissance" 
            label={t.prophylaxes} 
            value={fiche?.["Suivi sanitaire"]?.length || 0} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="achat" 
            label={t.reproductions} 
            value={fiche?.["Suivi reproduction"]?.length || 0} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="vente" 
            label={t.events} 
            value={fiche?.["Historique événements"]?.length || 0} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="zakat" 
            label={t.age} 
            value={fiche?.["Informations générales"]?.age || t.notProvided} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="mortalite" 
            label={t.status} 
            value={fiche?.["Informations générales"]?.statut || t.active} 
            variant="dashboard" 
            showIcon={false} 
          />
        </div>
      </div>

      {/* Section Informations générales */}
      <div className="fiche-section">
        <h2 className="section-title">Informations générales</h2>
        <div className="kpi-grid-fiche">
          <StatCard 
            type="effectif-initial" 
            label="MATRICULE" 
            value={fiche?.["Informations générales"]?.matricule || "Non renseigné"} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="naissance" 
            label="TROUPEAU" 
            value={(() => {
              // Essayer depuis la fiche d'abord
              if (fiche?.["Informations générales"]?.troupeau) {
                return fiche["Informations générales"].troupeau;
              }
              // Essayer depuis troupeauData
              if (troupeauData?.nom) {
                return troupeauData.nom;
              }
              // Essayer depuis animalData
              if (animalData?.troupeau) {
                if (typeof animalData.troupeau === 'object' && animalData.troupeau.nom) {
                  return animalData.troupeau.nom;
                }
              }
              return "Non assigné";
            })()}
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="achat" 
            label="RACE" 
            value={fiche?.["Informations générales"]?.race || "Non renseigné"} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="vente" 
            label="SEXE" 
            value={fiche?.["Informations générales"]?.sexe || "Non renseigné"} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="zakat" 
            label="CATÉGORIE" 
            value={fiche?.["Informations générales"]?.catégorie || "Non renseigné"} 
            variant="dashboard" 
            showIcon={false} 
          />
          <StatCard 
            type="mortalite" 
            label="ROBE" 
            value={fiche?.["Informations générales"]?.robe || "Non renseigné"} 
            variant="dashboard" 
            showIcon={false} 
          />
        </div>
      </div>

      {/* Section responsable troupeau */}
      <div className="fiche-section">
        <h2 className="section-title">Berger</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Nom :</span>
            <span className="info-value">{getBergerName()}</span>
          </div>
        </div>
      </div>

      {/* Section Ranch de provenance */}
      <div className="fiche-section">
        <h2 className="section-title">Ranch de provenance</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Nom :</span>
            <span className="info-value">{getRanchProvenanceName()}</span>
          </div>
        </div>
      </div>

      {/* Section Généalogie */}
      <div className="fiche-section">
        <h2 className="section-title">Informations généalogiques</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Géniteur :</span>
            <span className="info-value">
              {(() => {
                // Essayer depuis la fiche d'abord
                if (fiche?.["Informations généalogiques"]?.geniteur && 
                    fiche["Informations généalogiques"].geniteur !== "Non défini" &&
                    fiche["Informations généalogiques"].geniteur !== null) {
                  return fiche["Informations généalogiques"].geniteur;
                }
                // Essayer depuis animalData
                if (animalData?.pere) {
                  if (typeof animalData.pere === 'object' && animalData.pere.matricule) {
                    return animalData.pere.matricule;
                  } else if (typeof animalData.pere === 'number') {
                    const pereAnimal = animaux.find(a => a.id === animalData.pere);
                    if (pereAnimal) {
                      return pereAnimal.matricule;
                    }
                  }
                }
                return "Non défini";
              })()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Génitrice :</span>
            <span className="info-value">
              {(() => {
                // Essayer depuis la fiche d'abord
                if (fiche?.["Informations généalogiques"]?.genitrice && 
                    fiche["Informations généalogiques"].genitrice !== "Non définie" &&
                    fiche["Informations généalogiques"].genitrice !== null) {
                  return fiche["Informations généalogiques"].genitrice;
                }
                // Essayer depuis animalData
                if (animalData?.mere) {
                  if (typeof animalData.mere === 'object' && animalData.mere.matricule) {
                    return animalData.mere.matricule;
                  } else if (typeof animalData.mere === 'number') {
                    const mereAnimal = animaux.find(a => a.id === animalData.mere);
                    if (mereAnimal) {
                      return mereAnimal.matricule;
                    }
                  }
                }
                return "Non définie";
              })()}
            </span>
          </div>
          <div className="info-item full-width">
            <span className="info-label">Descendants :</span>
            <span className="info-value">
              {fiche["Informations généalogiques"]?.descendants && fiche["Informations généalogiques"].descendants.length > 0
                ? fiche["Informations généalogiques"].descendants.join(", ")
                : "Aucun"}
            </span>
          </div>
        </div>
      </div>

      {/* Section Suivi sanitaire */}
      <div className="fiche-section">
        <h2 className="section-title">Prophylaxie</h2>
        {fiche?.["Suivi sanitaire"] && fiche["Suivi sanitaire"].length > 0 ? (
          <div className="details-list">
            {fiche["Suivi sanitaire"].map((s, index) => (
              <div key={index} className="detail-card">
                <div className="detail-header">
                  <span className="detail-type">{s.Type}</span>
                  <span className="detail-date">{s.Date}</span>
                </div>
                <div className="detail-content">
                  <div className="detail-row">
                    <span className="detail-label">Produit :</span>
                    <span className="detail-text">{s.Produit || "Non renseigné"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Prochaine intervention :</span>
                    <span className="detail-text">{s["Prochaine intervention"] || "Non renseigné"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-message">Aucune Prophylaxie enregistrée.</p>
        )}
      </div>

      {/* Section Suivi de reproduction */}
      <div className="fiche-section">
        <h2 className="section-title">Reproduction</h2>
        {fiche?.["Suivi reproduction"] && fiche["Suivi reproduction"].length > 0 ? (
          <div className="details-list">
            {fiche["Suivi reproduction"].map((r, index) => (
              <div key={index} className="detail-card">
                <div className="detail-header">
                  <span className="detail-type">Cycle #{index + 1}</span>
                  <span className={`status-badge status-${r.Statut?.toLowerCase() || "actif"}`}>
                    {r.Statut}
                  </span>
                </div>
                <div className="detail-content">
                  <div className="detail-row">
                    <span className="detail-label">Date chaleur :</span>
                    <span className="detail-text">{r["Date chaleur"] || "Non renseigné"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Date saillie :</span>
                    <span className="detail-text">{r["Date saillie"] || "Non renseigné"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Date vêlage :</span>
                    <span className="detail-text">{r["Date vêlage"] || "Non renseigné"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Nombre de veaux :</span>
                    <span className="detail-text">{r["Nombre de veaux"] || "0"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-message">Aucune reproduction enregistrée.</p>
        )}
      </div>

      {/* Section Historique des événements */}
      <div className="fiche-section">
        <h2 className="section-title">Historique des événements</h2>
        {fiche?.["Historique événements"] && fiche["Historique événements"].length > 0 ? (
          <div className="details-list">
            {fiche["Historique événements"].map((e, index) => (
              <div key={index} className="detail-card">
                <div className="detail-header">
                  <span className="detail-type">{e.Type}</span>
                  <span className="detail-date">{e.Date}</span>
                </div>
                <div className="detail-content">
                  <div className="detail-row full-width">
                    <span className="detail-label">Détails :</span>
                    <span className="detail-text">{e.Détails || e.Description || "Non spécifié"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-message">Aucun événement enregistré.</p>
        )}
      </div>

      {/* Section Valeur */}
      <div className="fiche-section">
        <h2 className="section-title">Valeur</h2>
        <div className="kpi-grid-fiche">
          <StatCard 
            type="valeur" 
            label="VALEUR ESTIMÉE" 
            value={fiche?.["Valeur estimée"] ? `${fiche["Valeur estimée"].toLocaleString()} FCFA` : "Non renseigné"} 
            variant="dashboard" 
            showIcon={false} 
            className="card-tall" 
          />
        </div>
      </div>

      {/* Bouton retour */}
      <div className="actions-container">
        <ModernButton
          variant="secondary"
          icon={<FaArrowLeft />}
          onClick={() => navigate(-1)}
        >
          Retour
        </ModernButton>
      </div>
    </PageContainer>
  );
}